import { Button, Modal } from "antd";

import { useContext, useEffect, useState, useMemo } from "react";
import { AppContext } from "../../context/app-context";
import { TagTableData } from "../../models/countries-interface";
import Table, { ColumnsType } from "antd/es/table";
import EditTagPage from "./EditTagPage";
import { CountrySelect } from "../../components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { TagApi } from "../../apis/tag-api";
import { faLock } from "@fortawesome/pro-regular-svg-icons";
import { resolveIcon } from "../../utils/api";

import "./Tags.scss";

const TagsPage = () => {
  const [ tagData, setTagData ] = useState<TagTableData[]>([]);

  const { isLoadingCountry, selectedCountry, profile } = useContext(AppContext);
  const [ editingId, setEditingId ] = useState<string|null>(null);

  const [ isLoadingTags, setLoadingTags ] = useState<boolean>(false);
  const [ firstLoadCompleted, setFirstLoadCompleted ] = useState<boolean>(false);
  const [, modalContextHolder] = Modal.useModal();

  const loadTags = () => {
	setLoadingTags(true);

	TagApi.getTags()
	  .then((tags: TagTableData[]) => { 

		setTagData(tags);
        setLoadingTags(false); 
		setFirstLoadCompleted(true);
      });

  };

  // load tag data
  useEffect(() => {
    if (editingId === null && !isLoadingTags && !firstLoadCompleted) {
      loadTags();
    } 
  }, [editingId, isLoadingTags, firstLoadCompleted]);
  
  // re-filter table data on country change
  const tableData = useMemo(() => {
	return tagData.filter(x => x.SupportedCountries.iv?.flatMap((x: any) => x.Data.Code).includes(selectedCountry?.code?.iv));
  }, [tagData, selectedCountry?.code?.iv]);

  const onNewTag = () => {
	if (selectedCountry !== null) {
	  setEditingId("0");
	}
  };

  const onLinkClick = (id: string) => {
    setEditingId(id);
  };

  const onExitEditPage = () => {
	setEditingId(null);
	loadTags();
  }

  const columns: ColumnsType<TagTableData> = [
    {
      title: 'Title',
      dataIndex: 'Title',
      key: 'title',
      render: (title, record) => {
		return (
		  <Button 
		    type="link" 
			onClick={() => {onLinkClick(record.Id)}}
			style={{
		      "display": "flex",
			  "gap": "8px",
			  "alignItems": "center"
			}}
		  >
		    <FontAwesomeIcon icon={resolveIcon(record.Icon.iv)} /> 
			<span>
			  {title["en"]}
			</span>
			{ record.SupportedCountries.iv.length > 1 && (
			  <FontAwesomeIcon style={{ "color": "grey" }} icon={faLock} />
			)}
		  </Button>
		);
	  },
    },
    {
      title: 'Type',
      dataIndex: 'Type',
      key: 'type',
      render: (type) => <span>{type.iv}</span>,
    },
  ];

  if (!profile || !profile.TagsManagementEnabled) {
	return null;
  }

  return (
    <>
      { editingId === null && 
		<>
		  <div className="content-header resources-header">		  
			<h2>{'Filters'}</h2>
			{ !isLoadingTags && (
			  <span>{`(${tableData.length} Total Filters)`}</span>
			)}
			<span className="row-right"></span>
			{ profile?.Market === "All" && (
			  <>
			    <span className="row-right">{'Country / Market:'}</span>
				<CountrySelect />
			  </>
			)}
			{modalContextHolder}
			<Button 
			  onClick={onNewTag}
		      type="primary" 
			  icon={<FontAwesomeIcon icon={['far', 'plus']} />}
			  disabled={tableData.length >= 8} // limit markets to 8 tags total
			>
			  {'New Filter'}
			</Button>
	      </div>
          <div className="content-body tags-body">
            <Table 
		      className="tags-table"
			  pagination={false}
			  columns={columns} 
			  dataSource={tableData}
			  loading={isLoadingCountry || isLoadingTags || !firstLoadCompleted}
			  rowKey={(x) => x.Id}
		    />
          </div>
        </>
	  }
      { editingId !== null &&
        <EditTagPage
          onExit={onExitEditPage}
		  setEditingId={setEditingId}
		  existingTagProp={tableData.find(x => x.Id === editingId) ?? null}
        />
      }
    </>
  );
}

export default TagsPage;
